import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Box,
  ImageList,
  ImageListItem,
  Button,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ConstructionIcon from '@mui/icons-material/Construction';
import DesignServicesIcon from '@mui/icons-material/DesignServices';

/**
 * Barrierefreie Version der MediaCard-Komponente.
 * - Semantische Section mit aria-labelledby
 * - Status mit role="status"
 * - Buttons und Bilder mit aria-label
 * - Galerie-Overlay als Dialog mit Fokus-Falle
 */
export default function MediaCardAccessible({ project, idx, cardLG, setCardLG }) {
  const prefersReducedMotion = useReducedMotion();
  const [height, setHeight] = useState('70vh');
  const [imgCols, setImgCols] = useState(window.innerWidth < 1200 ? 1 : 3);
  const [isLarge, setIsLarge] = useState(false);
  const [overlayImage, setOverlayImage] = useState(null);
  const cardRef = useRef(null);

  // Responsive columns
  useEffect(() => {
    const onResize = () => setImgCols(window.innerWidth < 1200 ? 1 : 3);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll into view when expanded/collapsed
  const toggleGallery = () => {
    if (!isLarge) {
      setHeight('100%');
      setCardLG(cardLG.map((v, i) => (i === idx ? 12 : v)));
      setIsLarge(true);
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    } else {
      setHeight('70vh');
      setCardLG(cardLG.map((v, i) => (i === idx ? 6 : v)));
      setIsLarge(false);
      setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  };

  return (
    <section
      ref={cardRef}
      role="region"
      aria-labelledby={`card-title-${idx}`}
      style={{ width: '100%' }}
    >
      <Card sx={{ height, width: '100%', position: 'relative', overflow: 'auto' }}>
        {/* Status Badge */}
        <Box
          role="status"
          aria-live="polite"
          sx={{ position: 'absolute', top: 10, left: 10, bgcolor: project.status.toLowerCase().includes('im bau') || project.status.toLowerCase().includes('in planung') ? '#00ba69' : 'grey.600', color: 'white', px: 1.5, py: 0.5, borderRadius: 1, fontWeight: 'bold', zIndex: 10, boxShadow: 1 }}
        >
          {project.status.toLowerCase().includes('im bau') || project.status.toLowerCase().includes('in planung')
            ? 'Verfügbar'
            : 'Nicht verfügbar'}
        </Box>

        {/* Titelbild */}
        <CardMedia
          component="img"
          src={project.titleImg}
          alt={
            project.title
              ? `Bild des Projekts ${project.title}`
              : 'Projektbild'
          }
          sx={{ width: '100%' }}
        />

        <CardContent>
          {/* Action Buttons */}
          <CardActions
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              gap: 1,
              mb: 2,
            }}
          >
            {project.immoscout && (
              <Button
                variant="contained"
                href={project.immoscout}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Exposé auf ImmoScout24 öffnen"
                sx={{
                  bgcolor: '#00ffd0',
                  color: 'black',
                  '&:hover': { bgcolor: '#CCFFF5' },
                  textTransform: 'none',
                }}
              >
                <b>Exposé auf ImmoScout24</b>
              </Button>
            )}
            <Button
              component={Link}
              to={`/project/20250${idx}`}
              variant="contained"
              aria-label={`Mehr zum Objekt ${project.title}`}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: '#0076c9',
                left: -8,
                mr: -1
              }}
            >
              Mehr zum Objekt
            </Button>
            {project.images?.length > 0 && (
              <Button
                variant="outlined"
                onClick={toggleGallery}
                aria-expanded={isLarge}
                aria-controls={`gallery-${idx}`}
                aria-label={isLarge ? 'Galerie verstecken' : 'Galerie anzeigen'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  bgcolor: 'black',
                  color: 'white',
                  '&:hover': { bgcolor: '#393E46' }                 
                }}
              >
                {isLarge ? 'Verstecke Galerie' : 'Zeige Galerie'}
              </Button>
            )}
          </CardActions>


          {/* Projekttitel mit Icon */}
          <Typography
            id={`card-title-${idx}`}
            component="h2"
            variant="h5"
            gutterBottom
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={project.status} arrow>
                <span aria-hidden="true">
                  {{
                    verkauft: <RemoveCircleOutlineIcon sx={{ mr: 1 }} />, 
                    'im bau': <ConstructionIcon sx={{ mr: 1 }} />, 
                    abgeschlossen: <RemoveCircleOutlineIcon sx={{ mr: 1 }} />, 
                    'in planung': <DesignServicesIcon sx={{ mr: 1 }} />,
                  }[project.status.toLowerCase()]}
                </span>
              </Tooltip>
              {project.title}
            </Box>
          </Typography>

          {/* Beschreibung (HTML) */}
          <div
            dangerouslySetInnerHTML={{ __html: project.description }}
            aria-label="Projektbeschreibung"
          />

          {/* Galerie */}
          {isLarge && (
            <Box id={`gallery-${idx}`} sx={{ mt: 2 }}>
              <ImageList cols={imgCols} gap={8} sx={{ width: '100%' }}>
                {project.images.map((img, i) => (
                  <ImageListItem key={i}>
                    <motion.img
                      src={img}
                      alt={`Galeriebild ${i + 1} von ${project.title}`}
                      loading="lazy"
                      style={{ width: '100%', cursor: 'pointer' }}
                      onClick={() => setOverlayImage(img)}
                      whileFocus={
                        prefersReducedMotion ? {} : { scale: 1.02 }
                      }
                      tabIndex={0}
                      role="button"
                      aria-label={`Galeriebild ${i + 1} vergrößern`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setOverlayImage(img);
                        }
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Overlay-Dialog */}
      <Dialog
        open={Boolean(overlayImage)}
        onClose={() => setOverlayImage(null)}
        aria-labelledby={`dialog-title-${idx}`}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle id={`dialog-title-${idx}`}>Bildvorschau</DialogTitle>
        <IconButton
          aria-label="Dialog schließen"
          onClick={() => setOverlayImage(null)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          {overlayImage && (
            <Box
              component="img"
              src={overlayImage}
              alt={`Vergrößertes Bild des Projekts ${project.title}`}
              sx={{ width: '100%' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
